import {
  useState,
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import {
  ShoppingCart,
  Search,
  RefreshCw,
  Coins,
  Ban,
  Printer,
  LogOut,
  AlertTriangle,
  HandCoins,
} from 'lucide-react';
import { toast } from 'sonner';
import { registrarVentaOffline, type CartLine } from '@/lib/sales';
import { getUltimaVentaReimprimible } from '@/lib/sync-queue';
import { printThermalTicket } from '@/lib/ticket';
import { roundMoney } from '@mibodega/domain';
import {
  loadCatalog,
  readCatalog,
  writeCatalog,
  refreshCatalogDetailed,
  type CatalogItem,
} from '@/lib/catalog';
import { connector } from '@/lib/auth';
import {
  applyCartToCatalogStock,
  blockedSaleMessage,
  catalogForCashier,
  catalogStockKind,
  catalogStockSummaryLabel,
  isMaxReached,
  summarizeCatalogStock,
} from '@/lib/stock-display';
import { SyncIndicator } from '@/components/SyncIndicator';
import { CajaCerradaBanner } from '@/components/CajaCerradaBanner';
import { ProductCard } from '@/components/ProductCard';
import { SesionCajaAnteriorDialog } from '@/components/SesionCajaAnteriorDialog';
import { UserBadge } from '@/components/UserBadge';
import { AnularVentaModal } from '@/components/AnularVentaModal';
import { OverflowMenu, type OverflowMenuItem } from '@/components/OverflowMenu';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/Button';
import { Chip, ChipOutline } from '@/components/Chip';

import { debeOfrecerAperturaInicial, getSesionCajaActiva, type SesionCaja } from '@/lib/cash';
import { CashSessionModal } from '@/components/CashSessionModal';
import { CheckoutModal, type CheckoutConfirm } from '@/components/CheckoutModal';
import {
  refreshOrgConfig,
  readOrgConfig,
  rateIsStale,
  findTasa,
  bodegaNombre,
  type OrgConfig,
} from '@/lib/org-config';
import { flushAbonosQueue, flushCajaQueue } from '@/lib/sync-queue';
import { createClient } from '@supabase/supabase-js';
import { formatMoney } from '@/lib/format';
import { loadCustomers, readCustomers, refreshCustomers, type Customer } from '@/lib/customers';
import { pagoCubreTotal, pagosCuadran, sumaPagos } from '@/lib/payments';
import { getPrintTicket } from '@/lib/print-setting';
import { PostSaleStrip } from '@/components/PostSaleStrip';
import { TextField } from '@/components/TextField';
import { CartPanel } from '@/components/CartPanel';
import { CategoryNav } from '@/components/CategoryNav';
import { CobrarCreditoModal } from '@/components/CobrarCreditoModal';
import {
  buildCatalogSections,
  filterCatalogByCategory,
  type CatalogCategorySelection,
} from '@/lib/category-filter';

type SalePageProps = {
  onLogout?: () => void;
};

export function SalePage({ onLogout }: SalePageProps = {}) {
  const [productos, setProductos] = useState<CatalogItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<CatalogCategorySelection>('all');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [monedaCodigo, setMonedaCodigo] = useState('COP');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sesionCaja, setSesionCaja] = useState<SesionCaja | null>(null);
  const [isCashModalOpen, setIsCashModalOpen] = useState(false);
  const [postSale, setPostSale] = useState<{
    consecutivo: string;
    total: number;
    itemsCount: number;
    numeroFacturaServidor?: string;
  } | null>(null);
  const [ventasHoyCount, setVentasHoyCount] = useState(0);
  const [ventasHoyTotal, setVentasHoyTotal] = useState(0);
  const [isAnularOpen, setIsAnularOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  /** Distingue "cargando" de "vacío real" para no asustar al cajero. */
  const [catalogPhase, setCatalogPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [orgConfig, setOrgConfig] = useState<OrgConfig | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isAbonoOpen, setIsAbonoOpen] = useState(false);
  const [bloqueoCajaAnterior, setBloqueoCajaAnterior] = useState(false);
  const [cajaHidratada, setCajaHidratada] = useState(false);
  const yaOfrecioApertura = useRef(false);
  const [forceCashModalCerrar, setForceCashModalCerrar] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [focusedCardIdx, setFocusedCardIdx] = useState(0);
  const lastPulseRef = useRef<number>(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const announceTimerRef = useRef<number | undefined>(undefined);
  const catalogRequestRef = useRef(0);
  // Stable element so TextField doesn't see a new leadingIcon identity every
  // keystroke (defensive — controlled input already keeps focus, but a fresh
  // React node here used to re-layout the adornment on every render).
  const searchLeadingIcon = useMemo(() => <Search className="h-4 w-4" aria-hidden="true" />, []);

  // MIB-48 — Identidad del cajero en el header. connector.getCurrentUser()
  // es estable en este ciclo de vida (sale de localStorage en mount); leer
  // una vez es suficiente y evita renders extra cuando cambia otro state.
  const currentUser = useMemo(() => connector.getCurrentUser(), []);

  const orgId = connector.getCurrentOrgId();
  const sucursalId =
    sesionCaja?.organization_id === orgId
      ? sesionCaja.sucursal_id
      : connector.getCurrentSucursalId();
  const catalogScopeRef = useRef({ orgId, sucursalId });
  catalogScopeRef.current = { orgId, sucursalId };

  function isCurrentCatalogRequest(
    requestId: number,
    requestOrgId: string | null,
    requestSucursalId: string | null,
  ): boolean {
    const currentScope = catalogScopeRef.current;
    return (
      requestId === catalogRequestRef.current &&
      requestOrgId === currentScope.orgId &&
      requestSucursalId === currentScope.sucursalId
    );
  }

  function announce(msg: string) {
    window.clearTimeout(announceTimerRef.current);
    setAnnouncement('');
    announceTimerRef.current = window.setTimeout(() => setAnnouncement(msg), 30);
  }

  function isSesionAnterior(sesion: SesionCaja): boolean {
    const apertura = new Date(sesion.fecha_apertura);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    apertura.setHours(0, 0, 0, 0);
    return apertura < hoy;
  }

  // Si la orgConfig llega tarde, defaultear la moneda del carrito a base
  useEffect(() => {
    const base = orgConfig?.monedaBase;
    if (base) {
      setMonedaCodigo((prev) => (prev === 'COP' ? base : prev));
    }
  }, [orgConfig?.monedaBase]);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++catalogRequestRef.current;
    setIsRefreshing(false);
    setCatalogPhase('loading');
    // Do NOT wipe productos here. Clearing the catalog on every org/sucursal
    // identity flicker remounts the product grid while the cashier is typing
    // in search and feels like the search input "loses focus" (layout swap
    // empty ↔ grid). Prefer IDB snapshot first, then network replace.

    const storedSesion = getSesionCajaActiva();
    const sesion = storedSesion?.organization_id === orgId ? storedSesion : null;
    setSesionCaja(sesion);
    setBloqueoCajaAnterior(Boolean(sesion && isSesionAnterior(sesion)));
    setCajaHidratada(true);

    // Pre-popular desde IndexedDB para evitar el flash de "Catálogo vacío"
    // cuando hay datos en disco y todavía no terminó el read-through de la red.
    void readCatalog(orgId, sucursalId).then((snap) => {
      if (cancelled) return;
      if (snap && Array.isArray(snap.items) && snap.items.length > 0) {
        setProductos(snap.items);
      }
    });

    loadCatalog(orgId, sucursalId)
      .then((items) => {
        if (!cancelled && isCurrentCatalogRequest(requestId, orgId, sucursalId)) {
          setProductos(items);
          setCatalogPhase('ready');
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.warn('[sale-page] catalog load failed:', err);
          setCatalogPhase('error');
        }
      });

    if (orgId) {
      const cached = readOrgConfig(orgId);
      if (cached) setOrgConfig(cached);
      refreshOrgConfig(orgId)
        .then((config) => {
          if (!cancelled) setOrgConfig(config);
        })
        .catch((err: unknown) => {
          if (!cancelled) console.warn('[sale-page] org config load failed:', err);
        });
      loadCustomers(orgId)
        .then((loadedCustomers) => {
          if (!cancelled) setCustomers(loadedCustomers);
        })
        .catch((err: unknown) => {
          if (!cancelled) console.warn('[sale-page] customers load failed:', err);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [orgId, sucursalId]);

  // Si el primer load dejó el catálogo vacío (JWT aún no listo, race de sucursal),
  // reintenta una vez a los 1.5s sin intervención del cajero.
  useEffect(() => {
    if (catalogPhase !== 'ready' || productos.length > 0) return;
    const t = window.setTimeout(() => {
      const requestId = ++catalogRequestRef.current;
      void loadCatalog(orgId, sucursalId)
        .then(async (items) => {
          if (!isCurrentCatalogRequest(requestId, orgId, sucursalId)) return;
          if (items.length === 0) return;
          setProductos(items);
        })
        .catch((err: unknown) => {
          console.warn('[sale-page] catalog auto-retry failed:', err);
        });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [catalogPhase, productos.length, orgId, sucursalId]);

  async function refreshCatalogManual() {
    const requestId = ++catalogRequestRef.current;
    setIsRefreshing(true);
    setCatalogPhase('loading');
    try {
      const result = await refreshCatalogDetailed(orgId, sucursalId);
      if (!isCurrentCatalogRequest(requestId, orgId, sucursalId)) return;
      setProductos(result.items);
      setCatalogPhase('ready');
      if (orgId) {
        const nextCustomers = await refreshCustomers(orgId);
        setCustomers(nextCustomers);
        const cfg = await refreshOrgConfig(orgId);
        if (cfg) setOrgConfig(cfg);
      }
      if (result.source === 'cache') {
        toast.warning('No se pudo traer el catálogo del admin', {
          description:
            result.reason === 'no-session'
              ? 'La sesión expiró. Cerrá sesión y volvé a entrar, o reintentá en unos segundos.'
              : 'Se mantiene lo que ya había en este equipo. Revisá la red o el producto en el admin (tiene que tener presentación activa).',
        });
      } else if (result.items.length === 0) {
        toast.message('Catálogo sin productos', {
          description:
            'El admin no devolvió presentaciones activas. Verificá que el producto tenga unidad/precio.',
        });
      } else {
        const visibles = catalogForCashier(result.items).length;
        toast.success(
          visibles === 1
            ? 'Catálogo actualizado: 1 producto en esta bodega'
            : `Catálogo actualizado: ${visibles} productos en esta bodega`,
        );
      }
    } catch {
      if (isCurrentCatalogRequest(requestId, orgId, sucursalId)) {
        setCatalogPhase('error');
        toast.error('No se pudo refrescar el catálogo');
      }
    } finally {
      if (isCurrentCatalogRequest(requestId, orgId, sucursalId)) {
        setIsRefreshing(false);
      }
    }
  }

  async function flushPendingCaja() {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
    if (!SUPABASE_URL || !SUPABASE_ANON) return;
    const accessToken = await connector.getAccessToken();
    if (!accessToken) return;
    const sb = createClient(SUPABASE_URL, SUPABASE_ANON, {
      global: { headers: { Authorization: `Bearer ${accessToken}` } },
    });
    const rpcClient = {
      rpc: (fn: string, args: Record<string, unknown>) =>
        sb.rpc(fn, args) as unknown as Promise<{
          data?: unknown;
          error?: { message?: string } | null;
        }>,
      select: sb as never,
    };
    try {
      const abonos = await flushAbonosQueue(rpcClient);
      const { ok, fail } = await flushCajaQueue(rpcClient);
      if (abonos.ok + abonos.fail > 0) {
        toast[abonos.fail === 0 ? 'success' : 'warning'](
          `Abonos sync: ${abonos.ok} ok / ${abonos.fail} fallido(s)`,
        );
      }
      // Re-read local session in case adoptRemoteSesionAbierta rewrote it.
      const fresh = getSesionCajaActiva();
      if (fresh) setSesionCaja(fresh);
      if (ok + fail > 0) {
        toast[fail === 0 ? 'success' : 'warning'](`Caja sync: ${ok} ok / ${fail} fallido(s)`);
      }
    } catch (err) {
      console.warn('[sale-page] flush caja failed:', err);
    }
  }

  useEffect(() => {
    function onCashChanged() {
      setSesionCaja(getSesionCajaActiva());
    }
    window.addEventListener('mibodega:cash-session-changed', onCashChanged);
    return () => window.removeEventListener('mibodega:cash-session-changed', onCashChanged);
  }, []);

  useEffect(() => {
    if (navigator.onLine) {
      void flushPendingCaja();
    }
  }, [sesionCaja?.id]);

  // F2 enfoca el campo de búsqueda (acorde al helper text del input).
  // Ctrl+Enter dispara Cobrar cuando hay items en el carrito.
  // Esc dentro de un input NO debe burbujear para no cerrar un modal abierto.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
        if (cart.length === 0 || isCheckoutOpen) return;
        e.preventDefault();
        handleCompleteSale();
        return;
      }
      if (e.key !== 'F2') return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cart.length, isCheckoutOpen, sesionCaja]);

  function notifyBlocked(prod: CatalogItem) {
    const msg = blockedSaleMessage(prod.nombre, catalogStockKind(prod));
    if (!msg) return false;
    announce(msg);
    toast.warning(msg);
    return true;
  }

  function addToCart(prod: CatalogItem) {
    if (notifyBlocked(prod)) return;
    const stock = prod.stock_disponible;
    const currentQty = cartById.get(prod.presentacion_id)?.cantidad ?? 0;
    if (stock !== null && currentQty >= stock) {
      announce(
        stock <= 0
          ? `${prod.nombre}: sin stock disponible`
          : `Máximo disponible: ${stock} ${stock === 1 ? 'unidad' : 'unidades'} de ${prod.nombre}`,
      );
      return;
    }
    announce(`${prod.nombre} agregado, ${currentQty + 1} en carrito`);
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.presentacionId === prod.presentacion_id);
      if (idx >= 0) {
        const copy = [...prev];
        const current = copy[idx];
        const newQty = current.cantidad + 1;
        copy[idx] = {
          ...current,
          cantidad: newQty,
          subtotal: roundMoney(newQty * current.precioUnitario),
        };
        lastPulseRef.current = Date.now();
        return copy;
      }
      return [
        ...prev,
        {
          productoId: prod.id,
          productoNombre: prod.nombre,
          presentacionId: prod.presentacion_id,
          presentacionNombre: prod.presentacion_nombre,
          cantidad: 1,
          precioUnitario: prod.precio,
          subtotal: prod.precio,
        },
      ];
    });
  }

  // Sprint 99 POS.2 — drag-and-drop desde el catalogo al carrito.
  // El payload llega por dataTransfer (JSON con {id, presentacion_id});
  // buscamos el CatalogItem correspondiente en `filteredProducts` para
  // reutilizar el addToCart existente (que valida stock y emite announce).
  function handleProductDrop(presentacionId: string, _productId: string) {
    const prod = filteredProducts.find((p) => p.presentacion_id === presentacionId);
    if (prod) addToCart(prod);
  }

  function updateQuantity(presentacionId: string, delta: number) {
    if (delta > 0) {
      const prod = productos.find((p) => p.presentacion_id === presentacionId);
      const kind = prod ? catalogStockKind(prod) : 'sin_dato';
      if (prod && (kind === 'sin_inventario' || kind === 'agotado')) {
        notifyBlocked(prod);
        return;
      }
      const stock = prod?.stock_disponible ?? null;
      const currentQty = cartById.get(presentacionId)?.cantidad ?? 0;
      if (stock !== null && currentQty + delta > stock) {
        announce(
          `Máximo disponible: ${stock} ${stock === 1 ? 'unidad' : 'unidades'} de ${prod?.nombre ?? 'producto'}`,
        );
        return;
      }
    }
    setCart((prev) =>
      prev
        .map((l) => {
          if (l.presentacionId === presentacionId) {
            const newQty = l.cantidad + delta;
            if (newQty <= 0) return null;
            return {
              ...l,
              cantidad: newQty,
              subtotal: roundMoney(newQty * l.precioUnitario),
            };
          }
          return l;
        })
        .filter((l): l is CartLine => l !== null),
    );
    lastPulseRef.current = Date.now();
  }

  function removeFromCart(presentacionId: string) {
    setCart((prev) => prev.filter((l) => l.presentacionId !== presentacionId));
  }

  const subtotal = useMemo(() => roundMoney(cart.reduce((acc, l) => acc + l.subtotal, 0)), [cart]);
  const total = subtotal;

  const cartById = useMemo(() => {
    const map = new Map<string, CartLine>();
    cart.forEach((l) => map.set(l.presentacionId, l));
    return map;
  }, [cart]);

  const maxReachedIds = useMemo(() => {
    const s = new Set<string>();
    for (const l of cart) {
      const prod = productos.find((p) => p.presentacion_id === l.presentacionId);
      if (isMaxReached(prod?.stock_disponible ?? null, l.cantidad)) {
        s.add(l.presentacionId);
      }
    }
    return s;
  }, [cart, productos]);

  const branchCatalog = useMemo(() => catalogForCashier(productos), [productos]);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const categoryItems = filterCatalogByCategory(branchCatalog, selectedCategoryId);
    const matched = categoryItems.filter(
      (p) =>
        p.nombre.toLowerCase().includes(term) ||
        (p.codigo_barras && p.codigo_barras.includes(term)),
    );
    if (term.length > 0) return matched;
    const selected: CatalogItem[] = [];
    const rest: CatalogItem[] = [];
    const inCart = new Set(cart.map((l) => l.presentacionId));
    for (const p of matched) {
      if (inCart.has(p.presentacion_id)) selected.push(p);
      else rest.push(p);
    }
    selected.sort(
      (a, b) =>
        cart.findIndex((l) => l.presentacionId === a.presentacion_id) -
        cart.findIndex((l) => l.presentacionId === b.presentacion_id),
    );
    return [...selected, ...rest];
  }, [branchCatalog, selectedCategoryId, searchTerm, cart]);

  const catalogSections = useMemo(() => buildCatalogSections(branchCatalog), [branchCatalog]);

  useEffect(() => {
    if (selectedCategoryId === 'all') return;
    const remainsAvailable =
      selectedCategoryId === 'uncategorized'
        ? catalogSections.some((section) => section.id === 'uncategorized')
        : catalogSections.some(
            (section) =>
              section.id === selectedCategoryId ||
              section.children.some((child) => child.id === selectedCategoryId),
          );
    if (!remainsAvailable) setSelectedCategoryId('all');
  }, [catalogSections, selectedCategoryId]);

  // Roving tabindex: reset the active card index when the filter changes.
  // Do NOT depend on `productos` identity — catalog network refresh would
  // thrash the index while the cashier is typing, and combined with the
  // empty↔grid layout swap it steals focus from the search field.
  useEffect(() => {
    setFocusedCardIdx(0);
  }, [searchTerm, selectedCategoryId]);

  function gridColumnCount(): number {
    const el = gridRef.current;
    if (!el) return 2;
    const cols = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length;
    return Math.max(cols, 1);
  }

  function focusCard(idx: number) {
    setFocusedCardIdx(idx);
    cardRefs.current[idx]?.focus();
  }

  function handleCardKeyDown(e: ReactKeyboardEvent, idx: number) {
    const prod = filteredProducts[idx];
    if (!prod) return;
    const cols = gridColumnCount();
    const last = filteredProducts.length - 1;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusCard(Math.min(idx + 1, last));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusCard(Math.max(idx - 1, 0));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusCard(Math.min(idx + cols, last));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx - cols < 0) searchInputRef.current?.focus();
      else focusCard(idx - cols);
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      if (cartById.has(prod.presentacion_id)) updateQuantity(prod.presentacion_id, 1);
      else addToCart(prod);
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      updateQuantity(prod.presentacion_id, -1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setSearchTerm('');
      searchInputRef.current?.focus();
    }
  }

  function abrirCaja() {
    setForceCashModalCerrar(false);
    setIsCashModalOpen(true);
  }

  function handleCompleteSale() {
    if (cart.length === 0) return;
    const sesion = getSesionCajaActiva();
    if (!sesion) {
      // No deshabilitar el botón Cobrar: al clickear se abre el modal de
      // apertura para que el cajero entienda por qué no puede cobrar.
      toast.message('Abrí la caja para poder cobrar esta venta');
      abrirCaja();
      return;
    }
    setIsCheckoutOpen(true);
  }

  // Al pasar de caja abierta → cerrada, abrir de inmediato el modal de
  // apertura (no dejar al cajero con "Caja cerrada" sin saber qué
  // hacer). Antes exigía `cart.length > 0` y por eso al final del turno
  // —cuando el carrito ya está vacío— no se re-abría nada y el cajero
  // tenía que abrir el menú a mano. MIB-46.
  const prevSesionRef = useRef(sesionCaja);
  useEffect(() => {
    const prev = prevSesionRef.current;
    prevSesionRef.current = sesionCaja;
    if (prev && !sesionCaja && !bloqueoCajaAnterior) {
      setForceCashModalCerrar(false);
      setIsCashModalOpen(true);
      toast.message(
        cart.length > 0
          ? 'Caja cerrada. Abrí un turno nuevo para seguir cobrando.'
          : 'Caja cerrada · Abrí el nuevo turno para registrar las primeras ventas de hoy.',
      );
    }
  }, [sesionCaja, cart.length, bloqueoCajaAnterior]);

  useEffect(() => {
    if (
      !debeOfrecerAperturaInicial({
        cajaHidratada,
        haySesion: Boolean(sesionCaja),
        bloqueoAnterior: bloqueoCajaAnterior,
        yaOfrecido: yaOfrecioApertura.current,
        configLista: Boolean(orgConfig),
      })
    ) {
      return;
    }
    yaOfrecioApertura.current = true;
    setForceCashModalCerrar(false);
    setIsCashModalOpen(true);
  }, [cajaHidratada, sesionCaja, bloqueoCajaAnterior, orgConfig]);

  async function handleReimprimirUltimo() {
    try {
      const last = await getUltimaVentaReimprimible();
      if (!last || !last.lineas) {
        toast.info('No hay ventas sincronizadas con líneas guardadas todavía');
        return;
      }
      printThermalTicket({
        codigoVenta: last.codigo,
        fecha: last.fecha,
        monedaCodigo: last.monedaCodigo,
        subtotal: last.total,
        total: last.total,
        clienteNombre: last.clienteNombre,
        items: last.lineas.map((l) => ({
          nombre: `${l.productoNombre} (${l.presentacionNombre})`,
          cantidad: l.cantidad,
          precioUnitario: l.precioUnitario,
          subtotal: l.subtotal,
        })),
      });
      toast.success(`Reimprimiendo ${last.codigo}`);
    } catch (err) {
      console.warn('[sale-page] reimprimir fallo:', err);
      toast.error('No se pudo reimprimir el ticket');
    }
  }

  async function handleConfirmCheckout(data: CheckoutConfirm) {
    const sesion = getSesionCajaActiva();
    if (!sesion) {
      toast.error('Debes abrir la caja antes de registrar ventas');
      setIsCashModalOpen(true);
      return;
    }

    const totalVenta = cart.reduce((acc, l) => acc + l.subtotal, 0);
    // Defensa: no registrar contado con pagos por debajo del total del carrito.
    if (!data.esCredito) {
      const pagos = data.pagos ?? [];
      const ok =
        pagos.length > 1
          ? pagosCuadran(pagos, totalVenta)
          : pagos.length === 1 &&
            (pagos[0].metodo === 'DOLARES'
              ? pagoCubreTotal(Number(pagos[0].montoBase ?? 0), totalVenta)
              : pagoCubreTotal(Number(pagos[0].monto), totalVenta));
      if (!ok) {
        toast.error(
          `El pago (${sumaPagos(pagos)}) no cubre el total (${totalVenta}). No se registró la venta.`,
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const res = await registrarVentaOffline({
        organizationId: sesion.organization_id,
        sucursalId: sesion.sucursal_id,
        monedaCodigo,
        // IGTF se calcula en la RPC `confirmar_venta` por pago (v_pago->>'aplicaIgtf'
        // o métodos zelle/pago_movil/binance/usdt/paypal). El POS actual no usa
        // ninguno de esos métodos; queda fijo false. Ver MIB-24 para el alcance.
        aplicaIgtf: false,
        lineas: cart,
        pagos: data.pagos,
        clienteId: data.clienteId,
        esCredito: data.esCredito,
        imprimir: data.imprimir,
      });
      const itemsCount = cart.reduce((acc, l) => acc + l.cantidad, 0);

      // Burn sold qty from the local catalog snapshot so residual stock
      // updates immediately (server-side decrement happens on pos-upload).
      const nextProductos = applyCartToCatalogStock(productos, cart);
      setProductos(nextProductos);
      void writeCatalog(orgId, nextProductos, sucursalId).catch((err: unknown) => {
        console.warn('[sale-page] catalog stock cache write failed:', err);
      });

      setPostSale({
        consecutivo: res.consecutivo,
        total: totalVenta,
        itemsCount,
      });
      setVentasHoyCount((c) => c + 1);
      setVentasHoyTotal((t) => t + totalVenta);

      toast.success(
        navigator.onLine
          ? `Venta ${res.consecutivo} registrada · sincronizando…`
          : `Venta ${res.consecutivo} registrada offline · sube al reconectar`,
      );
      setCart([]);
      setIsCheckoutOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Error al registrar venta: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Header 3 zonas: Estado | Marca | Acciones.
          Destructive actions live in the overflow menu. Cobrar only when cart
          has items. Brand lives in the center so the cashier always knows
          which surface they're in. */}
      <header className="bg-neutral-0 flex shrink-0 flex-col gap-2 border-b border-neutral-200 px-4 py-2 sm:px-6">
        <div className="flex items-start justify-between gap-3">
          {currentUser && (
            <UserBadge
              user={currentUser}
              empresa={orgConfig?.nombre}
              bodega={bodegaNombre(orgConfig, sucursalId)}
            />
          )}
          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden items-center gap-2 sm:flex">
              <ShoppingCart className="text-primary h-4 w-4" aria-hidden="true" />
              <span className="text-title font-semibold text-neutral-900">MiBodega POS</span>
            </div>
            {(cart.length > 0 || !sesionCaja) && (
              <div className={cart.length > 0 ? 'hidden lg:block' : 'hidden md:block'}>
                <Button
                  variant="primary"
                  size="md"
                  onClick={cart.length > 0 ? handleCompleteSale : abrirCaja}
                  loading={isSubmitting}
                  loadingLabel="Registrando…"
                  disabled={isSubmitting}
                  title={
                    cart.length === 0
                      ? 'Abrir caja'
                      : !sesionCaja
                        ? 'Abrir caja para cobrar (Ctrl+Enter)'
                        : 'Cobrar (Ctrl+Enter)'
                  }
                  aria-describedby={!sesionCaja ? 'cobrar-help' : undefined}
                >
                  {cart.length === 0
                    ? 'Abrir caja'
                    : !sesionCaja
                      ? 'Abrir caja y cobrar'
                      : 'Cobrar'}
                </Button>
              </div>
            )}
            {cart.length > 0 && !sesionCaja && (
              <span id="cobrar-help" className="sr-only">
                Debes abrir la caja antes de cobrar. El botón abre el modal de apertura.
              </span>
            )}
            <ThemeToggle />
            <OverflowMenu
              ariaLabel="Más acciones de venta"
              items={
                [
                  {
                    id: 'refresh-catalog',
                    label: `Refrescar catálogo (${branchCatalog.length})`,
                    icon: <RefreshCw className="h-4 w-4" />,
                    onSelect: () => {
                      refreshCatalogManual().catch((err: unknown) => {
                        console.warn('[sale-page] manual catalog refresh failed:', err);
                      });
                    },
                    disabled: isRefreshing,
                  },
                  {
                    id: 'anular',
                    label: 'Anular venta',
                    icon: <Ban className="h-4 w-4" />,
                    onSelect: () => setIsAnularOpen(true),
                    destructive: true,
                  },
                  {
                    id: 'abono',
                    label: 'Cobrar crédito',
                    icon: <HandCoins className="h-4 w-4" />,
                    onSelect: () => setIsAbonoOpen(true),
                  },
                  {
                    id: 'caja',
                    label: sesionCaja ? 'Cerrar caja y arqueo' : 'Abrir caja',
                    icon: <Coins className="h-4 w-4" />,
                    onSelect: () => setIsCashModalOpen(true),
                  },
                  {
                    id: 'reimprimir',
                    label: 'Reimprimir último ticket',
                    icon: <Printer className="h-4 w-4" />,
                    onSelect: () => void handleReimprimirUltimo(),
                  },
                  ...(onLogout
                    ? [
                        {
                          id: 'logout',
                          label: 'Cerrar sesión',
                          icon: <LogOut className="h-4 w-4" />,
                          onSelect: () => onLogout(),
                        } satisfies OverflowMenuItem,
                      ]
                    : []),
                ] satisfies OverflowMenuItem[]
              }
            />
          </div>
        </div>
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label="Estado de la sesión"
        >
          <span
            aria-hidden="true"
            className={
              'h-2.5 w-2.5 shrink-0 rounded-full ' + (sesionCaja ? 'bg-success' : 'bg-neutral-400')
            }
          />
          <span className="text-xs font-semibold text-neutral-900">
            {sesionCaja ? 'Caja abierta' : 'Caja cerrada'}
          </span>
          {sesionCaja && (
            <span className="tnum font-mono text-xs text-neutral-500">
              Fondo{' '}
              {formatMoney(
                sesionCaja.monto_inicial,
                sesionCaja.moneda_base_codigo ?? orgConfig?.monedaBase ?? 'COP',
              )}
            </span>
          )}
          <SyncIndicator />
          {sesionCaja && isSesionAnterior(sesionCaja) && (
            <span className="bg-danger-soft text-danger flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              Caja del {new Date(sesionCaja.fecha_apertura).toLocaleDateString('es-CO')}
            </span>
          )}
          {ventasHoyCount > 0 && (
            <ChipOutline
              className="hidden sm:inline-flex"
              aria-label={`Hoy: ${ventasHoyCount} ${ventasHoyCount === 1 ? 'venta' : 'ventas'}, total ${formatMoney(ventasHoyTotal, monedaCodigo)}`}
            >
              <span className="tnum">Hoy: {ventasHoyCount}</span>
              <span aria-hidden="true">·</span>
              <span className="tnum">{formatMoney(ventasHoyTotal, monedaCodigo)}</span>
            </ChipOutline>
          )}
          {orgConfig && rateIsStale(orgConfig) && (
            <Chip tone="warning" className="hidden md:inline-flex">
              Tasas desactualizadas
            </Chip>
          )}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-3 sm:px-6">
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        {postSale && (
          <div className="shrink-0">
            <PostSaleStrip
              consecutivo={postSale.consecutivo}
              total={postSale.total}
              monedaCodigo={monedaCodigo}
              itemsCount={postSale.itemsCount}
              numeroFacturaServidor={postSale.numeroFacturaServidor}
              onReimprimir={() => void handleReimprimirUltimo()}
              onAnular={() => {
                setIsAnularOpen(true);
                setPostSale(null);
              }}
              onDismiss={() => setPostSale(null)}
            />
          </div>
        )}

        {!sesionCaja && !bloqueoCajaAnterior && <CajaCerradaBanner onAbrir={abrirCaja} />}

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden lg:grid-cols-12">
          {/* Catálogo de Productos (Izquierda) */}
          <div className="flex min-h-0 flex-col overflow-hidden lg:col-span-7">
            <div className="shrink-0">
              <TextField
                ref={searchInputRef}
                id="pos-catalog-search"
                type="text"
                label="Buscar producto"
                hideLabel
                placeholder="Buscar por nombre o código de barras..."
                leadingIcon={searchLeadingIcon}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' && filteredProducts.length > 0) {
                    e.preventDefault();
                    focusCard(0);
                    return;
                  }
                  if (e.key !== 'Enter') return;
                  // Barcode scanners envían Enter tras el código. Si hay
                  // match exacto por codigo_barras, lo agregamos al carrito.
                  // Si no, agregamos el primer resultado del filtro (flujo POS).
                  const term = searchTerm.trim();
                  if (!term) return;
                  const exact = branchCatalog.find(
                    (p) => p.codigo_barras && p.codigo_barras === term,
                  );
                  const target = exact ?? filteredProducts[0];
                  if (target) {
                    e.preventDefault();
                    addToCart(target);
                    setSearchTerm('');
                  }
                }}
              />
              <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
                <span>
                  <Kbd>F2</Kbd> buscar
                </span>
                <span>
                  <Kbd>Enter</Kbd> agregar
                </span>
                <span className="hidden sm:inline">
                  <Kbd>↑↓←→</Kbd> navegar
                </span>
                <span className="hidden sm:inline">
                  <Kbd>+</Kbd>
                  <Kbd>−</Kbd> cantidad
                </span>
                <span>
                  <Kbd>Ctrl</Kbd>+<Kbd>Enter</Kbd> cobrar
                </span>
              </p>
              <CategoryNav
                sections={catalogSections}
                selectedId={selectedCategoryId}
                onChange={(id) => {
                  setSelectedCategoryId(id);
                  announce(
                    id === 'all'
                      ? 'Mostrando todos los productos'
                      : `Categoría ${catalogSections.flatMap((section) => [section, ...section.children]).find((category) => category.id === id)?.name ?? 'seleccionada'}`,
                  );
                }}
              />
              {branchCatalog.length > 0 && (
                <p className="mt-2 text-xs text-neutral-600" role="status">
                  {catalogStockSummaryLabel(summarizeCatalogStock(branchCatalog))}
                </p>
              )}
            </div>

            {productos.length === 0 ? (
              <div
                role="status"
                className="mt-3 flex flex-col items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-8 text-center text-sm text-neutral-500"
              >
                <Search className="h-8 w-8 text-neutral-300" aria-hidden="true" />
                {catalogPhase === 'loading' || isRefreshing ? (
                  <>
                    <p className="font-semibold text-neutral-700">Cargando catálogo…</p>
                    <p className="max-w-sm text-xs">
                      Descargando productos desde Supabase. Si ya vendiste antes en este equipo,
                      aparecerán en cuanto responda la red.
                    </p>
                  </>
                ) : catalogPhase === 'error' ? (
                  <>
                    <p className="font-semibold text-neutral-700">No se pudo cargar el catálogo</p>
                    <p className="max-w-sm text-xs">
                      Revisá la conexión y tocá <strong>Refrescar catálogo</strong> en el menú ⋮. El
                      cache local no se borra si el servidor responde vacío por error de sesión.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-neutral-700">Catálogo vacío</p>
                    <p className="max-w-sm text-xs">
                      Toca <strong>Refrescar catálogo</strong> en el menú ⋮. Si estás offline y
                      nunca cargaste productos en este navegador, necesitás internet al menos una
                      vez.
                    </p>
                  </>
                )}
              </div>
            ) : branchCatalog.length === 0 ? (
              <div
                role="status"
                className="mt-3 flex flex-col items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-8 text-center text-sm text-neutral-500"
              >
                <Search className="h-8 w-8 text-neutral-300" aria-hidden="true" />
                <p className="font-semibold text-neutral-700">
                  Esta bodega no tiene productos para vender
                </p>
                <p className="max-w-sm text-xs">
                  El administrador todavía no asignó inventario a esta sucursal. Cuando lo asigne,
                  los productos aparecen acá.
                </p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div
                role="status"
                className="mt-3 flex flex-col items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-8 text-center text-sm text-neutral-500"
              >
                <Search className="h-8 w-8 text-neutral-300" aria-hidden="true" />
                <p className="font-semibold text-neutral-700">Ningún producto coincide</p>
                <p className="max-w-sm text-xs">Probá con otro nombre o código de barras.</p>
              </div>
            ) : (
              <div
                ref={gridRef}
                role="group"
                aria-label="Catálogo de productos"
                className="mt-3 grid min-h-0 flex-1 auto-rows-max grid-cols-2 content-start gap-3 overflow-y-auto pr-1 sm:grid-cols-3"
              >
                {filteredProducts.map((prod, idx) => {
                  const line = cartById.get(prod.presentacion_id);
                  const qty = line?.cantidad ?? 0;
                  const selected = qty > 0;
                  const maxReached = isMaxReached(prod.stock_disponible, qty);
                  return (
                    <ProductCard
                      key={`${prod.id}-${prod.presentacion_id}`}
                      prod={prod}
                      qty={qty}
                      selected={selected}
                      maxReached={maxReached}
                      monedaCodigo={monedaCodigo}
                      tabIndex={idx === focusedCardIdx ? 0 : -1}
                      buttonRef={(el) => {
                        cardRefs.current[idx] = el;
                      }}
                      onKeyDown={(e) => handleCardKeyDown(e, idx)}
                      onAdd={() => addToCart(prod)}
                      onBlocked={() => notifyBlocked(prod)}
                      onIncrement={() => updateQuantity(prod.presentacion_id, 1)}
                      onDecrement={() => updateQuantity(prod.presentacion_id, -1)}
                      onDragStart={() => {
                        announce(`Arrastrando ${prod.nombre}`);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Carrito de Compras (Derecha) — desktop sidebar */}
          <div className="hidden min-h-0 flex-col overflow-hidden lg:col-span-5 lg:flex">
            <CartPanel
              variant="sidebar"
              cart={cart}
              monedaCodigo={monedaCodigo}
              monedasOperativas={orgConfig?.monedasOperativas ?? []}
              onCurrencyChange={setMonedaCodigo}
              onDecrement={(id) => updateQuantity(id, -1)}
              onIncrement={(id) => updateQuantity(id, 1)}
              onRemove={removeFromCart}
              onCobrar={handleCompleteSale}
              cobrarDisabled={isSubmitting}
              cobrando={isSubmitting}
              sesionActiva={!!sesionCaja}
              maxReachedIds={maxReachedIds}
              onProductDrop={handleProductDrop}
            />
          </div>
        </div>
      </div>

      {/* Mobile bottom sheet — solo visible <lg */}
      <CartPanel
        variant="sheet"
        cart={cart}
        monedaCodigo={monedaCodigo}
        monedasOperativas={orgConfig?.monedasOperativas ?? []}
        onCurrencyChange={setMonedaCodigo}
        onDecrement={(id) => updateQuantity(id, -1)}
        onIncrement={(id) => updateQuantity(id, 1)}
        onRemove={removeFromCart}
        onCobrar={handleCompleteSale}
        cobrarDisabled={isSubmitting}
        cobrando={isSubmitting}
        sesionActiva={!!sesionCaja}
      />

      {bloqueoCajaAnterior && sesionCaja && (
        <SesionCajaAnteriorDialog
          sesion={sesionCaja}
          onClose={() => {
            setBloqueoCajaAnterior(false);
            setForceCashModalCerrar(true);
            setIsCashModalOpen(true);
          }}
        />
      )}

      <CashSessionModal
        isOpen={isCashModalOpen}
        onClose={() => {
          setForceCashModalCerrar(false);
          setIsCashModalOpen(false);
        }}
        sesionActiva={sesionCaja}
        orgConfig={orgConfig}
        onRetryConfig={async () => {
          if (!orgId) return;
          const cfg = await refreshOrgConfig(orgId);
          setOrgConfig(cfg);
        }}
        onSessionChange={(sesion) => {
          setSesionCaja(sesion);
          if (sesion === null) {
            setBloqueoCajaAnterior(false);
          }
        }}
        forceMode={forceCashModalCerrar ? 'cerrar' : undefined}
      />

      <CheckoutModal
        isOpen={isCheckoutOpen}
        total={total}
        monedaCodigo={monedaCodigo}
        monedaBase={orgConfig?.monedaBase ?? 'COP'}
        tasaUSD={orgConfig ? findTasa(orgConfig, 'USD', orgConfig.monedaBase) : null}
        customers={customers}
        defaultPrint={getPrintTicket()}
        onClose={() => setIsCheckoutOpen(false)}
        onConfirm={handleConfirmCheckout}
      />
      <AnularVentaModal
        open={isAnularOpen}
        onClose={() => setIsAnularOpen(false)}
        organizationId={sesionCaja?.organization_id ?? ''}
      />
      <CobrarCreditoModal
        open={isAbonoOpen}
        onClose={() => setIsAbonoOpen(false)}
        customers={customers}
        orgId={orgId}
        monedaBase={orgConfig?.monedaBase ?? monedaCodigo}
        sesionCaja={sesionCaja}
        onNeedCaja={() => setIsCashModalOpen(true)}
        onDone={() => {
          const snap = readCustomers(orgId);
          if (snap) setCustomers(snap.items);
        }}
      />
    </div>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-xs border border-neutral-300 bg-neutral-100 px-1 py-px font-mono text-[10px] font-medium text-neutral-700">
      {children}
    </kbd>
  );
}
